import { createBrowserHistory } from 'history';
import { configureStore } from '@reduxjs/toolkit';
import { createReduxHistoryContext } from 'redux-first-history';
import { thunk } from 'redux-thunk';
import createRootReducer from './reducers';
import apiMiddleware from './middleware/apiMiddleware';
import { API } from './constants/index';

// Create the history context for redux-first-history
const { createReduxHistory, routerMiddleware, routerReducer } = createReduxHistoryContext({
  history: createBrowserHistory(),
  routerType: "router-7"
});

// Enhanced error handling middleware for API calls
const errorHandlingMiddleware = store => next => action => {
  try {
    return next(action);
  } catch (err) {
    console.error('Caught an exception in middleware:', err);
    // Dispatch an error action that can be handled by reducers
    store.dispatch({
      type: 'ERROR_OCCURRED',
      payload: {
        message: err.message,
        stack: process.env.NODE_ENV !== 'production' ? err.stack : null,
        actionType: action.type
      }
    });
    return;
  }
};

// Configure store and create history using Redux Toolkit's configureStore
const configureAppStore = (preloadedState) => {
  // Create the store using Redux Toolkit
  const store = configureStore({
    reducer: createRootReducer(routerReducer),
    preloadedState,
    middleware: (getDefaultMiddleware) => 
      getDefaultMiddleware({
        // Disable immutability checks to prevent state mutation errors
        immutableCheck: false,
        
        // Keep serializable checks but ignore specific paths
        serializableCheck: {
          // Ignore non-serializable values in these actions
          ignoredActions: [
            '@@router/LOCATION_CHANGE',
            API,
            'ERROR_OCCURRED'
          ],
          // Ignore non-serializable values in these paths
          ignoredPaths: [
            'router',
            'router.location',
            'payload.onLoad',
            'payload.onSuccess',
            'payload.onFailure',
            'error',
            'auth.user',
            'locale',
            'charts',
            'devices'
          ]
        }
      }).concat(routerMiddleware, errorHandlingMiddleware, apiMiddleware, thunk),
    devTools: process.env.NODE_ENV !== 'production'
  });

  // Create the history using the store
  const history = createReduxHistory(store);

  // Add global error handler for uncaught Promise rejections
  if (typeof window !== 'undefined') {
    window.addEventListener('unhandledrejection', (event) => {
      console.error('Unhandled Promise Rejection:', event.reason);
      store.dispatch({
        type: 'ERROR_OCCURRED',
        payload: {
          message: event.reason?.message || 'Unhandled Promise rejection',
          stack: process.env.NODE_ENV !== 'production' ? event.reason?.stack : null,
          actionType: 'UNHANDLED_PROMISE'
        }
      });
    });
  }

  // Return both store and history for use in index.js
  return { store, history };
};

export const loadState = () => {
  try {
    const serializedState = localStorage.getItem('state');
    if (serializedState === null) return {};
    
    const loadedState = JSON.parse(serializedState);
    
    // Return only the keys that your reducers expect
    return {
      charts: loadedState.charts || {},
      devices: loadedState.devices || {}
      // Add other keys your reducers expect
    };
  } catch (err) {
    console.error('Failed to load state from localStorage:', err);
    return {};
  }
};

// Enhanced state saving with error handling
export const saveState = state => {
  try {
    // Deep clone and sanitize the state before serializing
    const stateToSave = sanitizeStateForStorage(state);
    const serializedState = JSON.stringify(stateToSave);
    localStorage.setItem('state', serializedState);
  } catch (err) {
    console.error('Failed to save state to localStorage:', err);
    // Attempt to save a minimal version if full state fails
    try {
      const minimalState = { version: state.version || '1.0.0' };
      localStorage.setItem('state', JSON.stringify(minimalState));
    } catch (innerErr) {
      // Last resort - clear storage if even minimal save fails
      localStorage.removeItem('state');
    }
  }
};

// Helper function to ensure state is serializable
function sanitizeStateForStorage(state) {
  // Create a deep copy to avoid modifying original state
  const sanitizedState = JSON.parse(JSON.stringify({
    // Only select specific parts of the state to save
    charts: state.charts ? filterOfflineChartData(state.charts) : {},
    devices: state.devices || {},
  }));
  
  return sanitizedState;
}

export const filterOfflineChartData = charts => {
  // filter out the actual data readings so we only store the chart config
  try {
    return Object.keys(charts).reduce((acc, curr) => {
      !(curr in acc) && (acc[curr] = {});
      Object.keys(charts[curr]).forEach(chart => {
        acc[curr][chart] = { ...charts[curr][chart], data: [] };
      });
      return acc;
    }, {});
  } catch (err) {
    console.error('Error filtering chart data:', err);
    return {}; // Return empty object on error
  }
};

export default configureAppStore;